/**
 * lib/help-figures-harness — ТЕСТ/БИЛД-ХАРНЕСС для генерации скриншот-иллюстраций
 * «Помощи» + превью-скриншотов режимов (тикеты start-page-redo-and-help AC-19/26;
 * playtest-fixes AC-A2/B7; figures-v2 fix-cycle 3 AC-1..AC-9 owner playtest v4;
 * help-screens-phone fix-cycle 5 owner playtest v6 «халтура»).
 *
 * ⚠ КОНТРАКТ fix-cycle 5 (owner playtest v6, 2026-06-18): ВСЕ скрины «Помощи»
 *   снимаются с ОДНОГО телефона (единый phone-viewport 390×844 dpr2 в генераторе) —
 *   единый стиль + отсылка к кроссплатформе + доска некрупная. Доска-фигуры правил
 *   теперь несут ПЛАШКУ «Очки/Ходы» + НИШУ (как реальная партия), снимаются
 *   `.board-frame` (НЕ чистая `.board` — owner забраковал «фигуры без плашки/ниши»).
 *   Реальные экраны (реплей с КНОПКАМИ / доска дня без кнопок / лидерборд-таблица)
 *   снимаются с настоящих экранов на том же phone-viewport (см. app.ts demo-routes +
 *   scripts/gen-help-figures SCREEN_FIGS).
 *
 * ЗАЧЕМ / WHY THIS EXISTS:
 *   - «Помощь» иллюстрируется снимками РЕАЛЬНОЙ доски в заданном состоянии (ход /
 *     сбор линии / предсказание / длинная линия / двойной сбор); раскладки —
 *     ЛЕГАЛЬНЫЕ на движке (`docs/specs/help-figures-scenarios.md` §3, явные
 *     координаты, НЕ seed; F5 = L-узел — owner-claim «плюс-крест недостижим»);
 *   - карточки режимов на ГЛАВНОЙ (лендинг start-screen) показывают УМЕНЬШЁННЫЙ
 *     СКРИНШОТ каждого режима — РЕАЛЬНАЯ стартовая доска startState(THUMB_SEED) (= та
 *     же, что живая мини-доска «Играть») + ПЛАШКА счёта + НИША, рендер РОВНО как
 *     превью «Играть» (.board-frame, AC-4/AC-5). «Играть» (mode-select) сам показывает
 *     ЖИВУЮ board-view (mode-card), НЕ эти скрины (fix-cycle 4, ствол→ветки).
 *
 * ⚠ КОНТРАКТ ПЕРЕВЁРНУТ owner-playtest v4 (2026-06-18, тикет figures-v2):
 *   1. Фигуры «Помощи» = ПОЛНАЯ доска 9×9, видна ЦЕЛИКОМ (НЕ кадрированная область).
 *      Прошлый цикл КАДРИРОВАЛ по bbox в окне `.help-figure-crop` — owner ЗАБРАКОВАЛ.
 *      Кроп УБРАН: рисуем всю 9×9, паттерн подсвечен НА полной доске.
 *   2. Превью режимов вернули ПЛАШКУ счёта + нишу (прошлый цикл сделал board-only).
 *   Спека сохраняет координаты/легальность/очки 35, но её инструкция «кадрировать /
 *   не весь 9×9» (§3(e)/§5/§7/AC-7) ОТМЕНЕНА owner'ом.
 *
 * ПУРЕ-ДАННЫЕ (ствол→ветки, CLAUDE.md): раскладки + ход + alt живут как
 *   ТИПИЗИРОВАННАЯ КАРТА `HELP_FIGURES` (без DOM) — board-view монтируется ПО НИМ.
 *   Это (1) единый источник раскладок для генератора и тестов; (2) тестируемость в
 *   node без браузера (help-figures-spec-conformance / -with-plaque-niche);
 *   (3) раскладки описаны данными, а не магией в генераторе.
 *
 * ПОЛНАЯ ДОСКА 9×9 (AC-1): харнесс монтирует ЕДИНУЮ board-view 9×9 (один источник
 *   визуала шара/призрака/клетки), БЕЗ crop-окна. Генератор снимает доску ЦЕЛИКОМ
 *   (`.board` для фигур правил — чистая сетка; `.board-frame` для превью — с плашкой
 *   и нишей). Паттерн правила подсвечивается прямо на полной доске.
 *
 * ТРЕЙЛ ХОДА (AC-2): для фигур с ходом (F1/F2/F5) путь шара рисуется ЕДИНЫМ
 *   `findPath` движка (визуальный helper) поверх board-view — НЕ ad-hoc.
 *
 * SCOPE: пуре-данные раскладок/ходов/alt + монтаж board-view static + подсветка.
 *   NOT IN SCOPE: сама доска (lib/board-view), сетка/шары (board.css), генерация
 *   PNG (scripts/gen-help-figures), движок (shared/engine/lines98).
 *
 * Cross-references: спека help-figures-scenarios.md §3/AC-1..9; тикет figures-v2
 *   AC-1..AC-9 + fix-cycle 4 (restore-igrat-screenshots-v3); help-figures-legality /
 *   -spec-conformance / -with-plaque-niche (тесты); lib/board-view (mountBoardView/
 *   renderStaticGrid); shared/engine/lines98 (startState/findPath — расклад превью =
 *   живая доска «Играть», THUMB_SEED; трейл хода); screens/start-screen (MODE_SCREEN_SRC
 *   — статичные скрины лендинга ссылаются на public/modes/*.png отсюда).
 */

import '../styles/help-figures.css';

import { checkLines, findPath, startState } from '@lines98/shared/engine/lines98';
import { mountBoardView } from './board-view.js';
import { t } from './i18n/index.js';
import type { CellValue, ColorIndex, NextBall } from '@lines98/shared/types/game';

const N = 9;

/** Пустая сетка 9×9. */
function empty(): CellValue[][] {
  return Array.from({ length: N }, () => Array.from({ length: N }, () => 0 as CellValue));
}

/** Поставить шар цвета `color` в (r,c). */
function put(grid: CellValue[][], r: number, c: number, color: number): void {
  grid[r]![c] = color as CellValue;
}

/** Горизонтальная линия цвета `color`, ряд `r`, столбцы c0..c0+len-1. */
function hLine(grid: CellValue[][], r: number, c0: number, len: number, color: number): void {
  for (let i = 0; i < len; i++) put(grid, r, c0 + i, color);
}

/** Вертикальная линия цвета `color`, столбец `c`, ряды r0..r0+len-1. */
function vLine(grid: CellValue[][], c: number, r0: number, len: number, color: number): void {
  for (let i = 0; i < len; i++) put(grid, r0 + i, c, color);
}

/** Ход (старт→цель) — для трейла findPath + подсветки выбранного/цели. */
export interface FigureMove {
  readonly sr: number;
  readonly sc: number;
  readonly r: number;
  readonly c: number;
}

/**
 * ПУРЕ-данные одной фигуры (ствол): раскладка 9×9, ghost/ниша, ход, alt.
 * board-view рисуется ПО grid/ghosts (ПОЛНАЯ доска 9×9, AC-1 — БЕЗ кропа); трейл —
 * по move. (Кадр-кроп `bbox` УБРАН owner v4 — показываем всю доску целиком.)
 */
export interface HelpFigureSpec {
  /** Раскладка 9×9 (значения = colorIndex палитры; 0 = пусто). */
  readonly grid: CellValue[][];
  /** Ghost-маркеры предсказания (F3 / превью predict) — рисуются board-view. */
  readonly ghosts?: NextBall[];
  /** Ниша 3 будущих шаров — ТОЛЬКО для превью predict/classic (НЕ board-фигуры правил). */
  readonly niche?: NextBall[];
  /** Ход (F1/F2/F5) — трейл findPath + подсветка старта/цели. */
  readonly move?: FigureMove;
  /**
   * Показать результат хода ВИЗУАЛЬНО (шар прибыл в цель + собираемые линии
   * подсвечены) НЕ меняя `grid` (grid = состояние ДО хода — нужно тестам легальности
   * и spec-conformance: узел пуст до хода). 'after' = scoring-двойной-сбор (узел
   * заполнен, обе пятёрки «снимаются»); по умолчанию false = кадр-заготовка.
   */
  readonly resolved?: boolean;
  /**
   * Клетка статичной собираемой линии (F4 — нет хода, но линия уже в grid). checkLines
   * через неё подсвечивает всю линию; бейдж очков — на ней. Только для resolved-static.
   */
  readonly staticClearAt?: { readonly r: number; readonly c: number };
  /** Бейдж очков на фигуре (engine-truth: F2=«+5», F4=«+18», F5=«+35»). */
  readonly scoreBadge?: string;
  /** alt-текст ru (AC-F5/spec AC-9) — описывает ПРИЁМ, не «изображение». */
  readonly altRu: string;
  /** alt-текст en. */
  readonly altEn: string;
}

// =============================================================================
// КООРДИНАТЫ ФИГУР — строго по help-figures-scenarios.md §3 (явные, не seed).
// Цвета (A/B/G…) — индексы палитры board.css (bc1..bc7); важна РАЗЛИЧИМОСТЬ.
// =============================================================================

const A = 1; // условный цвет «красный» (движок цвето-агностичен к путям)
const B = 3; // условный цвет B (синий)
const G = 4; // условный цвет G (зелёный)

/**
 * Представительная ниша 3 будущих шаров для фигур ПРАВИЛ (fix-cycle 5): owner велел
 * показывать на доска-фигурах плашку «Очки/Ходы» + нишу — чтобы фигура выглядела как
 * реальная партия (а не голая сетка). Конкретные цвета не несут смысла (фигура правил
 * про паттерн на доске, не про очередь) — берём различимую тройку из палитры.
 */
const RULE_NICHE: NextBall[] = [
  { r: 0, c: 0, color: A as ColorIndex },
  { r: 0, c: 0, color: B as ColorIndex },
  { r: 0, c: 0, color: G as ColorIndex },
];

/** F1 — Ход (spec §3 F1): M(1,1)→J(1,4), путь по строке 1 свободен, без сбора. */
function figureMove(): HelpFigureSpec {
  const grid = empty();
  put(grid, 1, 1, A); // M — старт хода
  // фон-беспорядок (не образует линию, не блокирует путь): spec §3 F1 (b).
  put(grid, 2, 1, B);
  put(grid, 2, 3, A);
  put(grid, 4, 1, A);
  put(grid, 4, 4, B);
  return {
    grid,
    move: { sr: 1, sc: 1, r: 1, c: 4 },
    altRu: 'Ход: выбранный шар едет по свободному коридору пустых клеток в подсвеченную пустую клетку-цель на поле 9×9.',
    altEn: 'A move: the selected ball slides along a clear corridor of empty cells into the highlighted empty target cell on the 9×9 field.',
  };
}

/** F2 — Сбор линии (spec §3 F2): M(2,3)→J(1,3) замыкает 5 в строке 1, +5. */
function figureLine(): HelpFigureSpec {
  const grid = empty();
  put(grid, 1, 1, A);
  put(grid, 1, 2, A);
  put(grid, 1, 4, A);
  put(grid, 1, 5, A);
  put(grid, 2, 3, A); // M — старт хода под разрывом
  return {
    grid,
    move: { sr: 2, sc: 3, r: 1, c: 3 },
    // grid = ДО хода (разрыв (1,3) пуст — нужно spec-conformance/legality); ВИЗУАЛЬНО
    // показываем результат: шар прибыл в (1,3) → пятёрка (1,1)…(1,5) «снимается».
    resolved: true,
    scoreBadge: '+5', // scoreFor(5) = 5
    altRu: 'Сбор линии: пятый шар въезжает в разрыв, выстраивая пять шаров одного цвета в ряд — линия исчезает, плюс пять очков.',
    altEn: 'Clearing a line: a fifth ball slides into the gap, forming five same-colour balls in a row — the line clears for five points.',
  };
}

/** F3 — Предсказание (spec §3 F3): 3 ghost-маркера будущих шаров в своих клетках. */
function figurePrediction(): HelpFigureSpec {
  const grid = empty();
  put(grid, 0, 1, A);
  put(grid, 2, 2, A);
  put(grid, 2, 4, B);
  put(grid, 4, 1, B);
  const ghosts: NextBall[] = [
    { r: 0, c: 3, color: B as ColorIndex },
    { r: 2, c: 0, color: A as ColorIndex },
    { r: 4, c: 3, color: G as ColorIndex },
  ];
  return {
    grid,
    ghosts,
    altRu: 'Режим «Стандарт»: три уменьшённых маркера-призрака показывают, в какие клетки придут следующие шары.',
    altEn: 'Standard mode: three smaller ghost markers show which cells the next balls will land in.',
  };
}

/** F4 — Длинная линия (spec §3 F4): 7 шаров одного цвета подряд перед сбором, +18. */
function figureLongLine(): HelpFigureSpec {
  const grid = empty();
  hLine(grid, 1, 1, 7, A); // (1,1)…(1,7)
  put(grid, 2, 2, B); // фон
  put(grid, 2, 5, G); // фон
  return {
    grid,
    // Статичный кадр «линия только что замкнулась» — без хода (grid уже содержит 7
    // в ряд). resolved → семёрка подсвечена «снимается» + бейдж очков на крайней клетке.
    resolved: true,
    staticClearAt: { r: 1, c: 1 }, // любая клетка линии (checkLines симметричен)
    scoreBadge: '+18', // scoreFor(7) = 18
    altRu: 'Длинная линия из семи шаров одного цвета перед сбором — длинные линии приносят намного больше очков.',
    altEn: 'A long line of seven same-colour balls just before clearing — long lines score far more points.',
  };
}

/**
 * F5 — Двойной сбор / L-узел (spec §3 F5, ИСПРАВЛЕНИЕ owner-claim): M(0,1)→J(1,1)
 * замыкает гориз-рукав (1,2..1,5) И верт-рукав (2..5,1). Узел на УГЛУ L (≤2 занятых
 * орто-соседа) → достижим (в отличие от замкнутого плюс-центра). checkLines
 * объединяет 9 уникальных клеток → scoreFor(9)=35 (engine-truth).
 *
 * `phase`: 'before' (strategy — кадр ДО хода, узел пуст) / 'after' (scoring — узел
 * заполнен, обе пятёрки собираются). Одна раскладка, два кадра (spec §6 «не
 * дублировать координаты»).
 */
function figureDouble(phase: 'before' | 'after'): HelpFigureSpec {
  // grid = состояние ДО хода для ОБОИХ кадров (узел (1,1) ПУСТ, M над узлом) — нужно
  // spec-conformance/legality (узел пуст до хода, ход легален). Различие before/after
  // — только ВИЗУАЛИЗАЦИЯ: 'after' (resolved) показывает шар прибывшим в узел и обе
  // пятёрки «снимаются»; 'before' (заготовка) — узел подсвечен как цель.
  const grid = empty();
  hLine(grid, 1, 2, 4, A); // гориз-рукав (1,2)(1,3)(1,4)(1,5)
  vLine(grid, 1, 2, 4, A); // верт-рукав (2,1)(3,1)(4,1)(5,1)
  put(grid, 0, 1, A); // M — над узлом (кадр ДО хода)
  const move: FigureMove = { sr: 0, sc: 1, r: 1, c: 1 };
  if (phase === 'before') {
    return {
      grid,
      move,
      altRu: 'Заготовка двойного сбора: две линии одного цвета сходятся в угловом узле — один шар сверху замкнёт сразу обе.',
      altEn: 'A double-clear setup: two same-colour lines meet at a corner node — one ball from above will close both at once.',
    };
  }
  return {
    grid,
    move,
    resolved: true,
    scoreBadge: '+35', // engine-truth: 9 уникальных клеток = scoreFor(9) = 35
    altRu: 'Двойной сбор: один ход замыкает горизонтальную и вертикальную линии в угловом узле — снимаются девять клеток разом, плюс тридцать пять очков.',
    altEn: 'A double clear: one move closes the horizontal and vertical lines at the corner node — nine cells clear at once for thirty-five points.',
  };
}

// =============================================================================
// Превью режимов (AC-4/AC-5) — РЕАЛЬНАЯ стартовая доска + ПЛАШКА счёта + НИША под
// 3 шара, рендер РОВНО как превью «Играть» (.board-frame). Различие режимов =
// ТОЛЬКО подсказки (канон режимов, AC-5): Предсказание — ghost НА доске + ниша-
// очередь; Классика — ниша-очередь, ghost НЕ на доске; Вслепую — без подсказок.
//
// ⚠ ДОСКА = startState(THUMB_SEED) — ТОТ ЖЕ seed, что живая мини-доска «Играть»
// (components/mode-card THUMB_SEED), fix-cycle 4 owner-playtest v5: «скрин обязан
// совпадать с живой доской Играть». Hand-crafted modeBoard() УБРАН — раньше скрин
// расходился с живой доской. Доска + ниша во всех трёх ОДИНАКОВЫ (= st.grid/st.next),
// различие только подсказками. THUMB_SEED == mode-card.THUMB_SEED (0x10b98) — единый
// представительный стартовый расклад; не daily-seed (статика, не зачётная партия).
// =============================================================================

/** Точный seed мини-доски «Играть» (components/mode-card.THUMB_SEED) — единый расклад. */
const THUMB_SEED = 0x10b98;

/**
 * Карта ПУРЕ-данных фигур (ствол). Ключи = [[FIGURE]]-слаги копирайта (content/*.md)
 * + превью режимов (mode-preview-*). board-view монтируется по этим данным.
 *
 * Имена ключей фигур правил — стабильные слаги статей (how-to-play / scoring /
 * strategy / modes); previews — mode-preview-<level>.
 */
export const HELP_FIGURES: Record<string, HelpFigureSpec> = {
  // ── Фигуры правил (board-фигуры, ПОЛНАЯ доска 9×9, БЕЗ ниши; кроп УБРАН v4) ────
  'how-to-play-move': figureMove(), // F1
  'how-to-play-line': figureLine(), // F2 (how-to-play «Линии и пересечения»)
  'how-to-play-prediction': figurePrediction(), // F3
  'scoring-line': figureLine(), // F2 (scoring «Несколько линий за ход» — сбор пятёрки)
  'scoring-long-line': figureLongLine(), // F4
  'scoring-double': figureDouble('after'), // F5 после хода (двойной сбор, +35)
  'strategy-double-setup': figureDouble('before'), // F5 до хода (заготовка)
  // ── Превью режимов (AC-4/AC-5): startState(THUMB_SEED) (= живая доска «Играть») +
  //    ПЛАШКА счёта + НИША, как превью «Играть». Различие = ТОЛЬКО подсказки. Доска
  //    (st.grid) и очередь (st.next) ОДНИ на все три (AC-5; совпадают с «Играть»). ──
  ...modePreviewFigures(),
  // ── Иллюстрации статьи «Режимы» (fix-cycle 6, owner playtest v7): трио
  //    modes-{predict,classic,blind} из ОДНОГО seed (THUMB_SEED) — одна доска + одна
  //    очередь на все три, различие ТОЛЬКО подсказками. Снимаются phone board-frame
  //    (счёт+ниша), как остальные доска-фигуры. (Прежний единичный modes-prediction
  //    заменён этим трио.) ──
  ...modeArticleFigures(),
};

/**
 * Три превью режимов из ЕДИНОГО startState(THUMB_SEED) — побайтно тот же расклад, что
 * живая мини-доска «Играть» (mode-card). Различие режимов = только подсказки (канон):
 *   · predict — ghost (st.next) прямо в клетках доски + та же очередь в нише;
 *   · classic — очередь ТОЛЬКО в нише, ghost на доске НЕТ;
 *   · blind   — без подсказок (ни ghost, ни ниша).
 * Возвращает запись-карту (spread в HELP_FIGURES) — раскладка не дублируется.
 */
function modePreviewFigures(): Record<string, HelpFigureSpec> {
  const st = startState(THUMB_SEED);
  const grid = st.grid as CellValue[][];
  const next = st.next as NextBall[];
  return {
    'mode-preview-predict': {
      ...modeHints(grid, next, 'predict'),
      altRu: 'Превью режима «Стандарт»: будущие шары видны прямо в клетках доски и в нише очереди.',
      altEn: 'Standard mode preview: upcoming balls shown right in the board cells and in the queue niche.',
    },
    'mode-preview-classic': {
      ...modeHints(grid, next, 'classic'),
      altRu: 'Превью режима «Классика»: очередь будущих шаров видна только в нише, не на доске.',
      altEn: 'Classic mode preview: the queue of upcoming balls is shown only in the niche, not on the board.',
    },
    'mode-preview-blind': {
      ...modeHints(grid, next, 'blind'),
      altRu: 'Превью режима «Вслепую»: подсказок нет — ни на доске, ни в нише.',
      altEn: 'Blind mode preview: no hints — neither on the board nor in the niche.',
    },
  };
}

/**
 * Подсказки одного режима поверх ОДНОЙ доски+очереди (канон режимов — единый источник
 * для preview-карточек «Играть» И иллюстраций статьи «Режимы», ствол→ветки, без
 * дублирования логики):
 *   · predict — ghost (next) прямо в клетках доски + та же очередь в нише;
 *   · classic — очередь ТОЛЬКО в нише, ghost на доске НЕТ;
 *   · blind   — без подсказок (ни ghost, ни ниша).
 * Возвращает grid + ghosts/niche; alt-текст добавляет вызывающий (preview vs статья).
 */
function modeHints(
  grid: CellValue[][],
  next: NextBall[],
  mode: 'predict' | 'classic' | 'blind',
): Pick<HelpFigureSpec, 'grid' | 'ghosts' | 'niche'> {
  if (mode === 'predict') return { grid, ghosts: next, niche: next };
  if (mode === 'classic') return { grid, niche: next };
  return { grid }; // blind — без подсказок
}

/**
 * Трио иллюстраций для статьи «Режимы» (fix-cycle 6, owner playtest v7) — ОДИН seed
 * (THUMB_SEED) на все три: одна доска + одна очередь, различие ТОЛЬКО подсказками
 * (через `modeHints`, тот же канон, что у preview-карточек). alt-тексты — под статью
 * (не «превью», а описание режима). Снимаются phone board-frame (счёт+ниша).
 * Возвращает запись-карту (spread в HELP_FIGURES).
 */
function modeArticleFigures(): Record<string, HelpFigureSpec> {
  const st = startState(THUMB_SEED);
  const grid = st.grid as CellValue[][];
  const next = st.next as NextBall[];
  return {
    'modes-predict': {
      ...modeHints(grid, next, 'predict'),
      altRu: 'Режим «Стандарт»: три следующих шара показаны прямо в тех клетках доски, где появятся, и продублированы в нише очереди.',
      altEn: 'Standard mode: the next three balls are shown right in the board cells where they will appear, and mirrored in the queue niche.',
    },
    'modes-classic': {
      ...modeHints(grid, next, 'classic'),
      altRu: 'Режим «Классика»: видна только короткая очередь следующих цветов в нише — позиции на доске не показаны.',
      altEn: 'Classic mode: only a short queue of upcoming colours is shown in the niche — their positions on the board are hidden.',
    },
    'modes-blind': {
      ...modeHints(grid, next, 'blind'),
      altRu: 'Режим «Вслепую»: подсказок нет вовсе — ни шаров на доске, ни очереди в нише.',
      altEn: 'Blind mode: no hints at all — neither balls on the board nor a queue in the niche.',
    },
  };
}

/**
 * Смонтировать харнесс фигуры по `?fig=<name>`.
 *
 * ПОЛНАЯ ДОСКА 9×9 (owner v4, AC-1): единая board-view 9×9 (один источник визуала)
 * монтируется ЦЕЛИКОМ, БЕЗ crop-окна. Паттерн правила подсвечивается прямо на полной
 * доске. Генератор снимает `.board-frame` ЦЕЛИКОМ (fix-cycle 5 owner v6):
 *   · фигуры ПРАВИЛ — `.board-frame` (доска + ПЛАШКА «Очки/Ходы» + НИША — как реальная
 *     партия; раньше снимали голую `.board` без плашки — owner забраковал);
 *   · превью РЕЖИМОВ — `.board-frame` (доска + ПЛАШКА счёта + НИША, как превью
 *     «Играть», AC-4/AC-5).
 *
 * Трейл хода (F1/F2/F5) — findPath движка поверх board-view (единый визуал пути).
 * Неизвестный fig → пустая доска (defensive).
 */
export function mountHelpFigureHarness(host: HTMLElement): void {
  host.innerHTML = '';
  const wrap = document.createElement('div');
  // Центрирование доски в окне генератора; padding кратен --m (без magic-px).
  wrap.style.cssText =
    'min-height:100dvh;display:flex;align-items:center;justify-content:center;padding:var(--m);box-sizing:border-box;';
  host.appendChild(wrap);

  const fig =
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('fig')
      : null) ?? '';
  const spec = HELP_FIGURES[fig];
  // «Режимные» фигуры (preview-карточки «Играть» + иллюстрации статьи «Режимы»):
  // ниша берётся ТОЛЬКО из preset (predict/classic несут очередь; blind — без неё),
  // подсветка-оверлей правил НЕ применяется. Всё прочее — фигура правила.
  const isModeFigure = fig.startsWith('mode-preview') || fig.startsWith('modes-');
  const isRule = !!spec && !isModeFigure;

  // ОБЩИЙ board-view (static) — НЕ своя доска (ствол→ветки). Полная 9×9. aria-подпись
  // фигуры — i18n (figures.figureAria) по активному locale, НЕ русский хардкод.
  const bv = mountBoardView(wrap, { ariaLabel: t('figures.figureAria') });
  const preset = spec ?? { grid: empty(), altRu: '', altEn: '' };
  bv.renderStaticGrid(preset.grid, preset.ghosts);
  bv.setScore(0);
  bv.setMoves(0);
  // Ниша 3 шаров (fix-cycle 5): режимные фигуры predict/classic несут свою очередь
  // (preset.niche), blind — без ниши ([]). ФИГУРЫ ПРАВИЛ показывают представительную
  // нишу RULE_NICHE (owner v6: доска-фигура = плашка «Очки/Ходы» + ниша, как реальная
  // партия, НЕ голая сетка). preset.niche задан → берём его; иначе для фигуры правила
  // → RULE_NICHE; для blind-режима (niche не задан, не правило) → [] (без ниши).
  const nicheToShow = preset.niche ?? (isRule ? RULE_NICHE : []);
  bv.renderNiche(nicheToShow, nicheToShow.length > 0);

  if (isRule && spec) {
    // Фигуры правил (owner v6): плашка «Очки/Ходы» + ниша ОСТАЮТСЯ видимыми (генератор
    // снимает `.board-frame` целиком). Раньше плашка пряталась — owner забраковал.
    // Подсветка паттерна на ПОЛНОЙ доске (AC-1/AC-2):
    // трейл хода (F1/F2/F5) — findPath движка (единый визуал пути), подсветка
    // старта/цели; статичная собранная линия без хода (F4) — линия + бейдж очков.
    if (spec.move) renderMoveTrail(bv.cells, spec);
    else if (spec.resolved && spec.staticClearAt) renderStaticClear(bv.cells, spec);
  }
}

/**
 * Нарисовать трейл хода (findPath) + подсветку старта/цели поверх клеток board-view.
 * Путь — ЕДИНЫЙ движковый `findPath` (визуальный helper), не ad-hoc. Подсветка —
 * data-атрибуты на клетках (стили — board.css figure-классы .cell[data-fig]).
 */
function renderMoveTrail(cells: readonly HTMLDivElement[], spec: HelpFigureSpec): void {
  const move = spec.move;
  if (!move) return;
  const cell = (r: number, c: number): HTMLDivElement | undefined => cells[r * N + c];

  if (spec.resolved) {
    // ВИЗУАЛИЗАЦИЯ РЕЗУЛЬТАТА (F2/F5-after): шар прибыл в цель → собираемые линии
    // подсвечены «снимается». grid в данных = ДО хода, поэтому after-grid строим
    // локально (копия + шар в цели) и зовём ЕДИНЫЙ checkLines движка (не ad-hoc).
    const color = spec.grid[move.sr]?.[move.sc] ?? 0;
    const afterGrid = spec.grid.map((row) => row.slice()) as CellValue[][];
    afterGrid[move.r]![move.c] = color as CellValue;
    // Нарисовать прибывший шар в клетке-цели (board-view рисует только grid=до-хода).
    const t = cell(move.r, move.c);
    if (t && color) t.innerHTML = `<div class="ball static bc${color}"></div>`;
    // Подсветить ВСЕ снимаемые клетки (checkLines = единое множество уникальных).
    for (const [r, c] of checkLines(afterGrid, move.r, move.c)) {
      cell(r, c)?.setAttribute('data-fig', 'clear');
    }
  } else {
    // ЗАГОТОВКА (F1/F5-before): старт «выбран», цель «сюда», трейл по findPath.
    cell(move.sr, move.sc)?.setAttribute('data-fig', 'start');
    cell(move.r, move.c)?.setAttribute('data-fig', 'target');
    const path = findPath(spec.grid, { r: move.sr, c: move.sc }, { r: move.r, c: move.c });
    for (const step of path ?? []) {
      if ((step.r === move.sr && step.c === move.sc) || (step.r === move.r && step.c === move.c)) {
        continue;
      }
      cell(step.r, step.c)?.setAttribute('data-fig', 'trail');
    }
  }

  // Бейдж очков (engine-truth) на цели — для scoring-фигур (F2/F5-after).
  if (spec.scoreBadge) {
    const t = cell(move.r, move.c);
    if (t) {
      const badge = document.createElement('span');
      badge.className = 'cell-fig-badge';
      badge.textContent = spec.scoreBadge;
      t.appendChild(badge);
    }
  }
}

/**
 * Подсветить статичную собранную линию (F4 — линия уже в grid, хода нет) + бейдж
 * очков. checkLines движка (единый источник) подсвечивает всю линию через любую её
 * клетку (staticClearAt). Бейдж — на этой клетке.
 */
function renderStaticClear(cells: readonly HTMLDivElement[], spec: HelpFigureSpec): void {
  const at = spec.staticClearAt;
  if (!at) return;
  const cell = (r: number, c: number): HTMLDivElement | undefined => cells[r * N + c];
  for (const [r, c] of checkLines(spec.grid, at.r, at.c)) {
    cell(r, c)?.setAttribute('data-fig', 'clear');
  }
  if (spec.scoreBadge) {
    const t = cell(at.r, at.c);
    if (t) {
      const badge = document.createElement('span');
      badge.className = 'cell-fig-badge';
      badge.textContent = spec.scoreBadge;
      t.appendChild(badge);
    }
  }
}
